import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, MessageCircle, MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { assetUrl } from "@/lib/api";
import { openLeadForm } from "@/components/LeadFormDialog";

export default function Footer({ brand, contact, social, footer }) {
  return (
    <footer id="contact" className="border-t border-[#1E2D42] bg-[#0B131F]" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <Reveal>
          <div className="mb-16 flex flex-col items-start justify-between gap-8 rounded-2xl border border-[#B8860B]/30 bg-gradient-to-br from-[#B8860B]/15 to-transparent p-10 sm:flex-row sm:items-center">
            <div>
              <h2 className="font-syne text-2xl font-bold tracking-tight text-[#F8FAFC] sm:text-3xl">
                Ready to bank <span className="text-[#E3B23C]">on us?</span>
              </h2>
              <p className="mt-2 text-sm text-[#8CA0B8]">Join 50,000+ partners earning on every transaction.</p>
            </div>
            <button onClick={openLeadForm} data-testid="footer-cta"
              className="flex items-center gap-2 rounded-full bg-[#B8860B] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#D9A441] hover:shadow-[0_0_40px_rgba(184,134,11,0.35)] active:scale-95">
              Become a Partner <ArrowUpRight size={16} />
            </button>
          </div>
        </Reveal>

        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              {brand.logoUrl ? (
                <img src={assetUrl(brand.logoUrl)} alt={brand.name} className="h-11 w-11 rounded-full bg-white/95 object-contain p-0.5" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#B8860B] to-[#9A6A15] font-syne text-xl font-bold text-white">
                  {brand.logoMark || "\u0950"}
                </span>
              )}
              <span className="font-syne text-xl font-bold tracking-tight text-[#F8FAFC]" data-testid="footer-brand-name">{brand.name}</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#8CA0B8]">{brand.tagline}</p>
            {footer.note && <p className="mt-3 font-mono text-xs text-[#E3B23C]/80">{footer.note}</p>}
            <div className="mt-6 flex gap-3">
              {social.linkedin && (
                <a href={social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="social-linkedin"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#8CA0B8] transition-colors duration-300 hover:border-[#E3B23C]/50 hover:text-[#E3B23C]">
                  <Linkedin size={15} />
                </a>
              )}
              {social.twitter && (
                <a href={social.twitter} target="_blank" rel="noreferrer" aria-label="Twitter" data-testid="social-twitter"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#8CA0B8] transition-colors duration-300 hover:border-[#E3B23C]/50 hover:text-[#E3B23C]">
                  <Twitter size={15} />
                </a>
              )}
              {social.youtube && (
                <a href={social.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#8CA0B8] transition-colors duration-300 hover:border-[#E3B23C]/50 hover:text-[#E3B23C]">
                  <Youtube size={15} />
                </a>
              )}
              {social.whatsapp && (
                <a href={social.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="social-whatsapp"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#8CA0B8] transition-colors duration-300 hover:border-[#E3B23C]/50 hover:text-[#E3B23C]">
                  <MessageCircle size={15} />
                </a>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E3B23C]/80">Explore</h4>
            <ul className="mt-5 space-y-3 text-sm text-[#8CA0B8]">
              <li><Link to="/about" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-about">About Us</Link></li>
              <li><a href="/#services" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-services">Services</a></li>
              <li><a href="/#pricing" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-pricing">Pricing</a></li>
              <li><a href="/#faq" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-faq">FAQ</a></li>
              <li><Link to="/login" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-login">Retailer Login</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E3B23C]/80">Legal</h4>
            <ul className="mt-5 space-y-3 text-sm text-[#8CA0B8]">
              <li><Link to="/terms" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-terms">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="transition-colors hover:text-[#E3B23C]" data-testid="footer-link-privacy">Privacy Policy</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E3B23C]/80">Head Office</h4>
            <ul className="mt-5 space-y-4 text-sm text-[#8CA0B8]">
              <li className="flex items-start gap-2.5" data-testid="footer-address">
                <MapPin size={15} className="mt-0.5 shrink-0 text-[#E3B23C]/70" />
                <span className="whitespace-pre-line leading-relaxed">{contact.address}</span>
              </li>
              <li className="flex items-center gap-2.5" data-testid="footer-phone">
                <Phone size={15} className="shrink-0 text-[#E3B23C]/70" />
                <a href={`tel:${contact.phone}`} className="transition-colors hover:text-[#E3B23C]">{contact.phone}</a>
              </li>
              <li className="flex items-center gap-2.5" data-testid="footer-email">
                <Mail size={15} className="shrink-0 text-[#E3B23C]/70" />
                <a href={`mailto:${contact.email}`} className="transition-colors hover:text-[#E3B23C]">{contact.email}</a>
              </li>
              {contact.mapUrl && (
                <li>
                  <a href={contact.mapUrl} target="_blank" rel="noreferrer" data-testid="footer-map-link"
                    className="inline-flex items-center gap-1.5 font-mono text-xs text-[#E3B23C]/80 transition-colors hover:text-[#E3B23C]">
                    View on Google Maps <ArrowUpRight size={12} />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-white/5 pt-7 sm:flex-row sm:items-center">
          <p className="font-mono text-xs text-[#5A6B82]" data-testid="footer-copyright">
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </p>
          <p className="font-mono text-xs text-[#3D4A5C]">Built on NPCI-certified rails · Made in India</p>
        </div>
      </div>
    </footer>
  );
}
